import React, { type FC, type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { IconArrowLeft, IconArrowRight } from '@bynder/icons';
import generateTestId from 'packages/helpers/testIdHelpers';
import {
    CollapseExpandButton,
    CollapseExpandButtonContainer,
    Divider,
    DividerContainer,
} from './CollapseExpandDivider.styled';
import MaybeTooltip from 'packages/common/MaybeTooltip';

type CollapseExpandDividerProps = {
    sidebar: MutableRefObject<HTMLElement>;
    mainContainer: MutableRefObject<HTMLElement>;
};

export const CollapseExpandDivider: FC<CollapseExpandDividerProps> = ({ sidebar, mainContainer }) => {
    const container = useRef<HTMLDivElement>(null);
    const testId = generateTestId('collapse-expand-divider');
    const [show, setShow] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const sidebarInitialStyles = useRef({
        isInitialized: false,
        width: 0,
    });

    const toggleCollapsed = useCallback(() => {
        setCollapsed((prev) => {
            setShow(false);

            return !prev;
        });
    }, []);

    useEffect(() => {
        if (!container.current) {
            return;
        }

        const el = container.current;

        const mouseenter = () => {
            setShow(true);
        };

        const mouseleave = () => {
            setShow(false);
        };

        el.addEventListener('mouseenter', mouseenter);
        el.addEventListener('mouseleave', mouseleave);

        return () => {
            if (el) {
                el.removeEventListener('mouseenter', mouseenter);
                el.removeEventListener('mouseleave', mouseleave);
            }
        };
    }, []);

    useEffect(() => {
        if (!sidebar.current || !mainContainer.current) {
            return;
        }

        if (!sidebarInitialStyles.current.isInitialized) {
            sidebarInitialStyles.current.isInitialized = true;
            sidebarInitialStyles.current.width = sidebar.current.offsetWidth;
        }

        if (collapsed) {
            sidebar.current.style.width = '0px';
            mainContainer.current.style.width = '100vw';
        } else {
            sidebar.current.style.width = `${sidebarInitialStyles.current.width}px`;
            mainContainer.current.style.width = `calc(100vw - ${sidebarInitialStyles.current.width}px)`;
        }
    }, [collapsed, sidebar, mainContainer]);

    return (
        <DividerContainer ref={container} {...testId}>
            <Divider show={show} />
            <CollapseExpandButtonContainer show={show} collapsed={collapsed}>
                <MaybeTooltip position="left" hasTooltip={show} content={collapsed ? 'Expand' : 'Collapse'}>
                    <CollapseExpandButton
                        icon={collapsed ? <IconArrowLeft /> : <IconArrowRight />}
                        onClick={toggleCollapsed}
                        label=""
                    />
                </MaybeTooltip>
            </CollapseExpandButtonContainer>
        </DividerContainer>
    );
};
